import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  homeBannersApi,
  HomeBanner,
  HomeBannerLinkType,
  HomeBannerPlatform,
  CreateHomeBannerPayload,
  UpdateHomeBannerPayload,
} from '../../api/homeBanners';
import { settingsApi, GlobalSetting } from '../../api/settings';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Loader2,
  Plus,
  BarChart3,
  Pencil,
  Trash2,
  ExternalLink,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

interface HomeBannersPageProps {
  isDark: boolean;
}

const GLOBAL_SETTING_KEY = 'showHomeBanners';

const LINK_TYPES: HomeBannerLinkType[] = ['external', 'internal'];

interface FormState {
  slug: string;
  title_uz: string;
  title_ru: string;
  title_kiril: string;
  subtitle_uz: string;
  subtitle_ru: string;
  subtitle_kiril: string;
  image_url: string;
  bg_color: string;
  bg_color_to: string;
  text_color: string;
  link_url: string;
  link_type: HomeBannerLinkType;
  cta_label_uz: string;
  cta_label_ru: string;
  cta_label_kiril: string;
  platforms: HomeBannerPlatform[];
  is_active: boolean;
  sort_order: number;
  starts_at: string;
  ends_at: string;
}

const emptyForm: FormState = {
  slug: '',
  title_uz: '',
  title_ru: '',
  title_kiril: '',
  subtitle_uz: '',
  subtitle_ru: '',
  subtitle_kiril: '',
  image_url: '',
  bg_color: '#0A7F7C',
  bg_color_to: '',
  text_color: '#FFFFFF',
  link_url: '',
  link_type: 'external',
  cta_label_uz: '',
  cta_label_ru: '',
  cta_label_kiril: '',
  platforms: ['ios', 'android'],
  is_active: true,
  sort_order: 0,
  starts_at: '',
  ends_at: '',
};

export default function HomeBannersPage({ isDark }: HomeBannersPageProps) {
  const navigate = useNavigate();
  const [banners, setBanners] = useState<HomeBanner[]>([]);
  const [globalSetting, setGlobalSetting] = useState<GlobalSetting | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [togglingGlobal, setTogglingGlobal] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [bannersData, settingData] = await Promise.all([
        homeBannersApi.list(),
        settingsApi.getByKey(GLOBAL_SETTING_KEY).catch(() => null),
      ]);
      setBanners(bannersData);
      setGlobalSetting(settingData);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch banners');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (b: HomeBanner) => {
    setEditingId(b.id);
    setForm({
      slug: b.slug,
      title_uz: b.title_uz,
      title_ru: b.title_ru ?? '',
      title_kiril: b.title_kiril ?? '',
      subtitle_uz: b.subtitle_uz ?? '',
      subtitle_ru: b.subtitle_ru ?? '',
      subtitle_kiril: b.subtitle_kiril ?? '',
      image_url: b.image_url ?? '',
      bg_color: b.bg_color,
      bg_color_to: b.bg_color_to ?? '',
      text_color: b.text_color,
      link_url: b.link_url ?? '',
      link_type: b.link_type,
      cta_label_uz: b.cta_label_uz ?? '',
      cta_label_ru: b.cta_label_ru ?? '',
      cta_label_kiril: b.cta_label_kiril ?? '',
      platforms: b.platforms ?? ['ios', 'android'],
      is_active: b.is_active,
      sort_order: b.sort_order,
      starts_at: b.starts_at ? b.starts_at.slice(0, 16) : '',
      ends_at: b.ends_at ? b.ends_at.slice(0, 16) : '',
    });
    setDialogOpen(true);
  };

  const buildPayload = () => {
    const base = {
      title_uz: form.title_uz.trim(),
      title_ru: form.title_ru.trim() || undefined,
      title_kiril: form.title_kiril.trim() || undefined,
      subtitle_uz: form.subtitle_uz.trim() || undefined,
      subtitle_ru: form.subtitle_ru.trim() || undefined,
      subtitle_kiril: form.subtitle_kiril.trim() || undefined,
      image_url: form.image_url.trim() || undefined,
      bg_color: form.bg_color || undefined,
      bg_color_to: form.bg_color_to.trim() || undefined,
      text_color: form.text_color || undefined,
      link_url: form.link_url.trim() || undefined,
      link_type: form.link_type,
      cta_label_uz: form.cta_label_uz.trim() || undefined,
      cta_label_ru: form.cta_label_ru.trim() || undefined,
      cta_label_kiril: form.cta_label_kiril.trim() || undefined,
      platforms: form.platforms,
      is_active: form.is_active,
      sort_order: Number(form.sort_order) || 0,
      starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : undefined,
      ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : undefined,
    };
    return base;
  };

  const handleSubmit = async () => {
    if (form.platforms.length === 0) {
      toast.error('Pick at least one platform');
      return;
    }
    if (!form.title_uz.trim()) {
      toast.error('title_uz is required');
      return;
    }
    if (!editingId && !form.slug.trim()) {
      toast.error('slug is required');
      return;
    }

    setSubmitting(true);
    try {
      if (editingId) {
        const payload: UpdateHomeBannerPayload = buildPayload();
        const updated = await homeBannersApi.update(editingId, payload);
        setBanners((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
        toast.success('Banner updated');
      } else {
        const payload: CreateHomeBannerPayload = {
          slug: form.slug.trim(),
          ...buildPayload(),
          title_uz: form.title_uz.trim(),
        };
        const created = await homeBannersApi.create(payload);
        setBanners((prev) => [created, ...prev]);
        toast.success('Banner created');
      }
      setDialogOpen(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save banner');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (b: HomeBanner, value: boolean) => {
    try {
      const updated = await homeBannersApi.update(b.id, { is_active: value });
      setBanners((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update');
    }
  };

  const handleDelete = async (b: HomeBanner) => {
    if (!window.confirm(`Delete banner "${b.title_uz}"? Click history will be removed too.`))
      return;
    try {
      await homeBannersApi.remove(b.id);
      setBanners((prev) => prev.filter((x) => x.id !== b.id));
      toast.success('Banner deleted');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete');
    }
  };

  const handleToggleGlobal = async (value: boolean) => {
    setTogglingGlobal(true);
    try {
      const updated = await settingsApi.update(GLOBAL_SETTING_KEY, value);
      setGlobalSetting(updated);
      toast.success(
        value ? 'Banners enabled globally' : 'Banners hidden on mobile',
      );
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update setting');
    } finally {
      setTogglingGlobal(false);
    }
  };

  const activeCount = useMemo(
    () => banners.filter((b) => b.is_active).length,
    [banners],
  );

  const previewGradient = useMemo(() => {
    if (form.bg_color_to) {
      return `linear-gradient(135deg, ${form.bg_color}, ${form.bg_color_to})`;
    }
    return form.bg_color;
  }, [form.bg_color, form.bg_color_to]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1
            className={cn(
              'text-2xl lg:text-3xl font-bold tracking-tight',
              isDark ? 'text-white' : 'text-[#0F172A]',
            )}
          >
            Home banners
          </h1>
          <p
            className={cn(
              'text-sm mt-1',
              isDark ? 'text-slate-400' : 'text-slate-500',
            )}
          >
            Carousel banners shown on the mobile home screen
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          New banner
        </Button>
      </div>

      {/* Global toggle */}
      <Card
        className={cn(
          'rounded-2xl border-0 transition-all duration-300',
          isDark
            ? 'bg-[#1E293B] shadow-lg shadow-black/10'
            : 'bg-white shadow-lg shadow-slate-200/50',
        )}
      >
        <div className="p-5 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'w-10 h-10 rounded-xl flex items-center justify-center',
                globalSetting?.value
                  ? 'bg-emerald-500/15 text-emerald-500'
                  : 'bg-amber-500/15 text-amber-500',
              )}
            >
              <AlertCircle className="w-5 h-5" />
            </div>
            <div>
              <p
                className={cn(
                  'text-sm font-semibold',
                  isDark ? 'text-white' : 'text-[#0F172A]',
                )}
              >
                Global switch (showHomeBanners)
              </p>
              <p
                className={cn(
                  'text-xs mt-0.5',
                  isDark ? 'text-slate-400' : 'text-slate-500',
                )}
              >
                When OFF — mobile receives an empty list regardless of
                individual banner flags. When ON — only{' '}
                <span className="font-semibold">{activeCount}</span> active
                banners are shown.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {togglingGlobal && (
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            )}
            <Switch
              checked={globalSetting?.value === true}
              disabled={togglingGlobal}
              onCheckedChange={handleToggleGlobal}
            />
          </div>
        </div>
      </Card>

      <Card
        className={cn(
          'rounded-2xl border-0 overflow-hidden transition-all duration-300',
          isDark
            ? 'bg-[#1E293B] shadow-lg shadow-black/10'
            : 'bg-white shadow-lg shadow-slate-200/50',
        )}
      >
        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" />
          </div>
        ) : banners.length === 0 ? (
          <div className="text-center p-12">
            <p
              className={cn(
                'text-sm',
                isDark ? 'text-slate-400' : 'text-slate-500',
              )}
            >
              No banners yet. Click "New banner" to create the first one.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[60px]">Preview</TableHead>
                <TableHead>Title (uz)</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Link</TableHead>
                <TableHead className="w-[80px]">Order</TableHead>
                <TableHead className="w-[80px]">Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {banners.map((b) => (
                <TableRow key={b.id}>
                  <TableCell>
                    <div
                      className="w-10 h-10 rounded-lg border border-slate-200/30"
                      style={{
                        background: b.bg_color_to
                          ? `linear-gradient(135deg, ${b.bg_color}, ${b.bg_color_to})`
                          : b.bg_color,
                      }}
                    />
                  </TableCell>
                  <TableCell className="font-medium max-w-[260px] truncate">
                    {b.title_uz}
                    {(b.platforms ?? []).length < 2 && (
                      <Badge variant="outline" className="ml-2 uppercase text-[10px]">
                        {(b.platforms ?? []).join(', ') || '—'}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{b.slug}</TableCell>
                  <TableCell>
                    {b.link_url ? (
                      <a
                        href={b.link_type === 'external' ? b.link_url : undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#0EA5A4] hover:underline text-sm max-w-[260px] truncate"
                      >
                        <Badge variant="outline" className="capitalize mr-1">
                          {b.link_type}
                        </Badge>
                        <span className="truncate">{b.link_url}</span>
                        {b.link_type === 'external' && (
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        )}
                      </a>
                    ) : (
                      <span
                        className={cn(
                          'text-xs',
                          isDark ? 'text-slate-500' : 'text-slate-400',
                        )}
                      >
                        —
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="tabular-nums">{b.sort_order}</TableCell>
                  <TableCell>
                    <Switch
                      checked={b.is_active}
                      onCheckedChange={(value) => handleToggleActive(b, value)}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/home-banners/${b.id}/stats`)}
                        title="Statistics"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(b)}
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(b)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className={cn(
            'max-w-3xl max-h-[90vh] overflow-y-auto',
            isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white',
          )}
        >
          <DialogHeader>
            <DialogTitle
              className={cn(isDark ? 'text-white' : 'text-[#0F172A]')}
            >
              {editingId ? 'Edit banner' : 'New banner'}
            </DialogTitle>
            <DialogDescription
              className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}
            >
              {editingId
                ? 'Update the banner content and appearance'
                : 'Create a new banner for the mobile home screen'}
            </DialogDescription>
          </DialogHeader>

          {/* Preview */}
          <div
            className="rounded-2xl px-5 py-6 flex items-center justify-between"
            style={{ background: previewGradient, color: form.text_color }}
          >
            <div className="min-w-0">
              <p className="text-lg font-bold truncate">
                {form.title_uz || 'Title preview'}
              </p>
              {form.subtitle_uz && (
                <p className="text-sm opacity-90 mt-1 line-clamp-2">
                  {form.subtitle_uz}
                </p>
              )}
            </div>
            {form.cta_label_uz && (
              <div
                className="ml-4 px-3 py-1.5 rounded-lg text-xs font-semibold flex-shrink-0"
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  color: form.text_color,
                }}
              >
                {form.cta_label_uz}
              </div>
            )}
          </div>

          {/* Identity row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                Slug *
              </Label>
              <Input
                value={form.slug}
                onChange={(e) =>
                  setForm((f) => ({ ...f, slug: e.target.value }))
                }
                placeholder="summer_promo"
                disabled={!!editingId}
                className={cn(
                  isDark
                    ? 'bg-slate-800 border-slate-700 text-white'
                    : 'bg-white',
                )}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                Sort order
              </Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) =>
                  setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))
                }
                className={cn(
                  isDark
                    ? 'bg-slate-800 border-slate-700 text-white'
                    : 'bg-white',
                )}
              />
            </div>
            <div className="flex items-center gap-3 pt-7">
              <Switch
                checked={form.is_active}
                onCheckedChange={(checked) =>
                  setForm((f) => ({ ...f, is_active: checked }))
                }
              />
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                Active
              </Label>
            </div>
          </div>

          {/* Platforms — Android sells nothing in-app (ADR 0014) */}
          <div className="space-y-2">
            <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
              Platforms
            </Label>
            <div className="flex items-center gap-6">
              {(['ios', 'android'] as HomeBannerPlatform[]).map((p) => (
                <div key={p} className="flex items-center gap-2">
                  <Switch
                    checked={form.platforms.includes(p)}
                    onCheckedChange={(checked) =>
                      setForm((f) => ({
                        ...f,
                        platforms: checked
                          ? [...f.platforms.filter((x) => x !== p), p]
                          : f.platforms.filter((x) => x !== p),
                      }))
                    }
                  />
                  <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                    {p === 'ios' ? 'iOS' : 'Android'}
                  </Label>
                </div>
              ))}
            </div>
            <p className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
              Premium / narx / to'lov haqidagi bannerlarni Android'da yoqmang — Google Play
              ilova ichida tashqi to'lovga yo'naltirishni taqiqlaydi.
            </p>
          </div>

          {/* Localized content (tabs) */}
          <Tabs defaultValue="uz" className="w-full pt-2">
            <TabsList>
              <TabsTrigger value="uz">O'zbek</TabsTrigger>
              <TabsTrigger value="ru">Русский</TabsTrigger>
              <TabsTrigger value="kiril">Кирилл</TabsTrigger>
            </TabsList>

            {(['uz', 'ru', 'kiril'] as const).map((lang) => (
              <TabsContent key={lang} value={lang} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label
                    className={cn(
                      isDark ? 'text-slate-300' : 'text-slate-700',
                    )}
                  >
                    Title {lang === 'uz' ? '*' : ''}
                  </Label>
                  <Input
                    value={form[`title_${lang}`]}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        [`title_${lang}`]: e.target.value,
                      }))
                    }
                    placeholder={
                      lang === 'uz'
                        ? "Yangi imkoniyat"
                        : lang === 'ru'
                          ? 'Новая возможность'
                          : 'Янги имконият'
                    }
                    className={cn(
                      isDark
                        ? 'bg-slate-800 border-slate-700 text-white'
                        : 'bg-white',
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    className={cn(
                      isDark ? 'text-slate-300' : 'text-slate-700',
                    )}
                  >
                    Subtitle
                  </Label>
                  <Textarea
                    value={form[`subtitle_${lang}`]}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        [`subtitle_${lang}`]: e.target.value,
                      }))
                    }
                    rows={2}
                    className={cn(
                      isDark
                        ? 'bg-slate-800 border-slate-700 text-white'
                        : 'bg-white',
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    className={cn(
                      isDark ? 'text-slate-300' : 'text-slate-700',
                    )}
                  >
                    CTA label
                  </Label>
                  <Input
                    value={form[`cta_label_${lang}`]}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        [`cta_label_${lang}`]: e.target.value,
                      }))
                    }
                    placeholder={
                      lang === 'uz'
                        ? "Ko'rish"
                        : lang === 'ru'
                          ? 'Подробнее'
                          : 'Кўриш'
                    }
                    className={cn(
                      isDark
                        ? 'bg-slate-800 border-slate-700 text-white'
                        : 'bg-white',
                    )}
                  />
                </div>
              </TabsContent>
            ))}
          </Tabs>

          {/* Colors */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
            <ColorField
              label="Background color"
              value={form.bg_color}
              onChange={(v) => setForm((f) => ({ ...f, bg_color: v }))}
              isDark={isDark}
            />
            <ColorField
              label="Background gradient end"
              value={form.bg_color_to}
              onChange={(v) => setForm((f) => ({ ...f, bg_color_to: v }))}
              isDark={isDark}
              optional
            />
            <ColorField
              label="Text color"
              value={form.text_color}
              onChange={(v) => setForm((f) => ({ ...f, text_color: v }))}
              isDark={isDark}
            />
          </div>

          {/* Media */}
          <div className="grid grid-cols-1 gap-4 pt-2">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                Image URL
              </Label>
              <Input
                value={form.image_url}
                onChange={(e) =>
                  setForm((f) => ({ ...f, image_url: e.target.value }))
                }
                placeholder="https://cdn.../banner.jpg"
                className={cn(
                  isDark
                    ? 'bg-slate-800 border-slate-700 text-white'
                    : 'bg-white',
                )}
              />
            </div>
          </div>

          {/* Link */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
            <div className="space-y-2 md:col-span-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                Link URL
              </Label>
              <Input
                value={form.link_url}
                onChange={(e) =>
                  setForm((f) => ({ ...f, link_url: e.target.value }))
                }
                placeholder="https://talabaa.uz/promo"
                className={cn(
                  isDark
                    ? 'bg-slate-800 border-slate-700 text-white'
                    : 'bg-white',
                )}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                Link type
              </Label>
              <Select
                value={form.link_type}
                onValueChange={(value) =>
                  setForm((f) => ({
                    ...f,
                    link_type: value as HomeBannerLinkType,
                  }))
                }
              >
                <SelectTrigger
                  className={cn(
                    isDark
                      ? 'bg-slate-800 border-slate-700 text-white'
                      : 'bg-white',
                  )}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LINK_TYPES.map((p) => (
                    <SelectItem key={p} value={p} className="capitalize">
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Schedule */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                Starts at (optional)
              </Label>
              <Input
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) =>
                  setForm((f) => ({ ...f, starts_at: e.target.value }))
                }
                className={cn(
                  isDark
                    ? 'bg-slate-800 border-slate-700 text-white'
                    : 'bg-white',
                )}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                Ends at (optional)
              </Label>
              <Input
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) =>
                  setForm((f) => ({ ...f, ends_at: e.target.value }))
                }
                className={cn(
                  isDark
                    ? 'bg-slate-800 border-slate-700 text-white'
                    : 'bg-white',
                )}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className={cn(
                isDark
                  ? 'border-slate-700 text-slate-300 hover:bg-slate-800'
                  : 'border-slate-200',
              )}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingId ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ColorField({
  label,
  value,
  onChange,
  isDark,
  optional = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  isDark: boolean;
  optional?: boolean;
}) {
  const isValid = !value || /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value);
  return (
    <div className="space-y-2">
      <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
        {label}
        {optional ? (
          <span className="text-slate-400 font-normal ml-1">(optional)</span>
        ) : null}
      </Label>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="h-10 w-12 rounded-md border border-slate-200 cursor-pointer bg-transparent"
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#0A7F7C"
          className={cn(
            'font-mono uppercase',
            !isValid && 'border-red-500',
            isDark
              ? 'bg-slate-800 border-slate-700 text-white'
              : 'bg-white',
          )}
        />
      </div>
    </div>
  );
}
